/**
 * Display preferences (per browser): theme, contrast, text size. Applied to <html> before the
 * first render so the page never flashes the wrong theme. Storage can be unavailable (private
 * mode, blocked site data); the app then follows the OS and still works.
 */
import { useSyncExternalStore } from 'react';

export type ThemePref = 'system' | 'light' | 'dark';
export type ContrastPref = 'system' | 'more';
export type TextScale = 1 | 1.125 | 1.25;

export interface DisplayPrefs {
  theme: ThemePref;
  contrast: ContrastPref;
  textScale: TextScale;
}

const KEY = 'xc.display';
export const DEFAULT_PREFS: DisplayPrefs = { theme: 'system', contrast: 'system', textScale: 1 };
export const TEXT_SCALES: { value: TextScale; label: string }[] = [
  { value: 1, label: 'ปกติ' },
  { value: 1.125, label: 'ใหญ่' },
  { value: 1.25, label: 'ใหญ่มาก' },
];

export function parsePrefs(raw: string | null): DisplayPrefs {
  if (!raw) return DEFAULT_PREFS;
  try {
    const v = JSON.parse(raw) as Partial<DisplayPrefs>;
    return {
      theme: v.theme === 'light' || v.theme === 'dark' ? v.theme : 'system',
      contrast: v.contrast === 'more' ? 'more' : 'system',
      textScale: v.textScale === 1.125 || v.textScale === 1.25 ? v.textScale : 1,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function read(): DisplayPrefs {
  try {
    return parsePrefs(localStorage.getItem(KEY));
  } catch {
    return DEFAULT_PREFS;
  }
}

let current: DisplayPrefs = typeof window === 'undefined' ? DEFAULT_PREFS : read();
const listeners = new Set<() => void>();

/** Reflect preferences on the root element (tokens.css keys off these attributes). */
export function applyPrefs(p: DisplayPrefs, root: HTMLElement = document.documentElement) {
  if (p.theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', p.theme);
  if (p.contrast === 'more') root.setAttribute('data-contrast', 'more');
  else root.removeAttribute('data-contrast');
  root.style.setProperty('--xc-text-scale', String(p.textScale));
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', effectiveTheme(p) === 'dark' ? '#0d1422' : '#f5f7fb');
}

export function effectiveTheme(p: DisplayPrefs = current): 'light' | 'dark' {
  if (p.theme !== 'system') return p.theme;
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function setPrefs(next: Partial<DisplayPrefs>) {
  current = { ...current, ...next };
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* not persisted; still applied for this page */
  }
  applyPrefs(current);
  listeners.forEach((l) => l());
}

export function initPrefs() {
  applyPrefs(current);
  // Another tab changed them.
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    current = parsePrefs(e.newValue);
    applyPrefs(current);
    listeners.forEach((l) => l());
  });
  // The OS theme changed while following it: refresh theme-color and subscribers.
  matchMedia?.('(prefers-color-scheme: dark)').addEventListener?.('change', () => {
    applyPrefs(current);
    listeners.forEach((l) => l());
  });
}

export function useDisplayPrefs(): DisplayPrefs {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
    () => DEFAULT_PREFS,
  );
}
